import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t, Trans } from "@lingui/macro";
import { useWeb3React } from "@web3-react/core";
import useSWR from "swr";
import { ethers } from "ethers";
import classes from "./ClpSwap.module.scss";
import { getContract } from "config/contracts";
import {
  adjustForDecimals,
  BASIS_POINTS_DIVISOR,
  CLP_COOLDOWN_DURATION,
  CLP_DECIMALS,
  DEFAULT_SLIPPAGE_AMOUNT,
  getBuyClpFromAmount,
  getBuyClpToAmount,
  getSellClpFromAmount,
  getSellClpToAmount,
  PLACEHOLDER_ACCOUNT,
  SECONDS_PER_YEAR,
  USD_DECIMALS,
  USDG_DECIMALS,
} from "lib/legacy";

import { useCadPrice } from "domain/legacy";

import TokenSelector from "../Exchange/TokenSelector";
import BuyInputSection from "../BuyInputSection/BuyInputSection";
import Tooltip from "../Tooltip/Tooltip";

import ReaderV2 from "abis/ReaderV2.json";
import RewardReader from "abis/RewardReader.json";
import VaultV2 from "abis/VaultV2.json";
import ClpManager from "abis/ClpManager.json";
import RewardTracker from "abis/RewardTracker.json";
import Vester from "abis/Vester.json";
import RewardRouter from "abis/RewardRouter.json";
import Token from "abis/Token.json";

import "./ClpSwap.css";
import SwapErrorModal from "./SwapErrorModal";
import { CANTO, getChainName, IS_NETWORK_DISABLED } from "config/chains";
import { callContract, contractFetcher } from "lib/contracts";
import { approveTokens, useInfoTokens } from "domain/tokens";
import { useLocalStorageByChainId, useLocalStorageSerializeKey } from "lib/localStorage";
import { getTokenInfo, getUsd } from "domain/tokens/utils";
import { bigNumberify, expandDecimals, formatAmount, formatAmountFree, parseValue } from "lib/numbers";
import { getToken, getTokens, getWhitelistedTokens } from "config/tokens";
import { useChainId } from "lib/chains";
import Button from "components/Button/Button";
import classNames from "classnames";
import { ModalBase } from "../Modals/ModalBase/ModalBase";
import ArrowIcon from "./assets/arrow.svg";
import { ActionButton } from "../Buttons/ActionButton/ActionButton";
import { getIcon } from "../../config/icons";
import { SLIPPAGE_BPS_KEY } from "../../config/localStorage";

const { AddressZero } = ethers.constants;

function getStakingData(stakingInfo) {
  if (!stakingInfo || stakingInfo.length === 0) {
    return;
  }

  const keys = ["stakedClpTracker", "feeClpTracker"];
  const data = {};
  const propsLength = 5;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    data[key] = {
      claimable: stakingInfo[i * propsLength],
      tokensPerInterval: stakingInfo[i * propsLength + 1],
      averageStakedAmounts: stakingInfo[i * propsLength + 2],
      cumulativeRewards: stakingInfo[i * propsLength + 3],
      totalSupply: stakingInfo[i * propsLength + 4],
    };
  }

  return data;
}

export default function ClpSwap(props) {
  const { presetIsBuying, setPendingTxns, onClose } = props;

  const [isBuying, setIsBuying] = useState(presetIsBuying);
  const { active, library, account } = useWeb3React();
  const { chainId } = useChainId();
  const tokens = getTokens(chainId);
  const whitelistedTokens = getWhitelistedTokens(chainId);
  const [swapValue, setSwapValue] = useState("");
  const [clpValue, setClpValue] = useState("");
  const [swapTokenAddress, setSwapTokenAddress] = useLocalStorageByChainId(
    chainId,
    `${isBuying ? "BuyClp" : "SellClp"}-swap-token-address`,
    AddressZero
  );
  const [isApproving, setIsApproving] = useState(false);
  const [isWaitingForApproval, setIsWaitingForApproval] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [anchorOnSwapAmount, setAnchorOnSwapAmount] = useState(true);
  const [feeBasisPoints, setFeeBasisPoints] = useState("");
  const [modalError, setModalError] = useState(false);

  const readerAddress = getContract(chainId, "Reader");
  const rewardReaderAddress = getContract(chainId, "RewardReader");
  const vaultAddress = getContract(chainId, "Vault");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  const stakedClpTrackerAddress = getContract(chainId, "StakedClpTracker");
  const feeClpTrackerAddress = getContract(chainId, "FeeClpTracker");
  const usdgAddress = getContract(chainId, "USDG");
  const clpManagerAddress = getContract(chainId, "ClpManager");
  const clpRewardRouterAddress = getContract(chainId, "ClpRewardRouter");

  const [savedSlippageAmount, setSavedSlippageAmount] = useLocalStorageSerializeKey(
    [chainId, SLIPPAGE_BPS_KEY],
    DEFAULT_SLIPPAGE_AMOUNT
  );
  const tokensForBalanceAndSupplyQuery = [stakedClpTrackerAddress, usdgAddress];

  const tokenAddresses = tokens.map((token) => token.address);
  const { data: tokenBalances } = useSWR(
    [`ClpSwap:getTokenBalances:${active}`, chainId, readerAddress, "getTokenBalances", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(library, ReaderV2, [tokenAddresses]),
    }
  );

  const { data: balancesAndSupplies } = useSWR(
    [
      `ClpSwap:getTokenBalancesWithSupplies:${active}`,
      chainId,
      readerAddress,
      "getTokenBalancesWithSupplies",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(library, ReaderV2, [tokensForBalanceAndSupplyQuery]),
    }
  );

  const { data: aums } = useSWR([`ClpSwap:getAums:${active}`, chainId, clpManagerAddress, "getAums"], {
    fetcher: contractFetcher(library, ClpManager),
  });

  const { data: totalTokenWeights } = useSWR(
    [`ClpSwap:totalTokenWeights:${active}`, chainId, vaultAddress, "totalTokenWeights"],
    {
      fetcher: contractFetcher(library, VaultV2),
    }
  );

  const tokenAllowanceAddress = swapTokenAddress === AddressZero ? nativeTokenAddress : swapTokenAddress;
  const { data: tokenAllowance } = useSWR(
    [active, chainId, tokenAllowanceAddress, "allowance", account || PLACEHOLDER_ACCOUNT, clpManagerAddress],
    {
      fetcher: contractFetcher(library, Token),
    }
  );

  const { data: lastPurchaseTime } = useSWR(
    [`ClpSwap:lastPurchaseTime:${active}`, chainId, clpManagerAddress, "lastAddedAt", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(library, ClpManager),
    }
  );

  const { data: clpBalance } = useSWR(
    [`ClpSwap:clpBalance:${active}`, chainId, feeClpTrackerAddress, "stakedAmounts", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(library, RewardTracker),
    }
  );

  const clpVesterAddress = getContract(chainId, "ClpVester");
  const { data: reservedAmount } = useSWR(
    [`ClpSwap:reservedAmount:${active}`, chainId, clpVesterAddress, "pairAmounts", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(library, Vester),
    }
  );

  const { cadPrice } = useCadPrice(chainId, { canto: chainId === CANTO ? library : undefined }, active);

  const rewardTrackersForStakingInfo = [stakedClpTrackerAddress, feeClpTrackerAddress];
  const { data: stakingInfo } = useSWR(
    [`ClpSwap:stakingInfo:${active}`, chainId, rewardReaderAddress, "getStakingInfo", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(library, RewardReader, [rewardTrackersForStakingInfo]),
    }
  );

  const stakingData = getStakingData(stakingInfo);

  const redemptionTime = lastPurchaseTime ? lastPurchaseTime.add(CLP_COOLDOWN_DURATION) : undefined;
  const inCooldownWindow = redemptionTime && parseInt(Date.now() / 1000) < redemptionTime;

  const clpSupply = balancesAndSupplies ? balancesAndSupplies[1] : bigNumberify(0);
  const usdgSupply = balancesAndSupplies ? balancesAndSupplies[3] : bigNumberify(0);
  let aum;
  if (aums && aums.length > 0) {
    aum = isBuying ? aums[0] : aums[1];
  }
  const clpPrice =
    aum && aum.gt(0) && clpSupply.gt(0)
      ? aum.mul(expandDecimals(1, CLP_DECIMALS)).div(clpSupply)
      : expandDecimals(1, USD_DECIMALS);

  const clpSupplyUsd = clpSupply.mul(clpPrice).div(expandDecimals(1, CLP_DECIMALS));

  let maxSellAmount = clpBalance;
  if (clpBalance && reservedAmount) {
    maxSellAmount = clpBalance.sub(reservedAmount);
  }

  const { infoTokens } = useInfoTokens(library, chainId, active, tokenBalances, undefined);
  const swapToken = getToken(chainId, swapTokenAddress);
  const swapTokenInfo = getTokenInfo(infoTokens, swapTokenAddress);

  const swapTokenBalance = swapTokenInfo && swapTokenInfo.balance ? swapTokenInfo.balance : bigNumberify(0);

  const swapAmount = parseValue(swapValue, swapToken && swapToken.decimals);
  const clpAmount = parseValue(clpValue, CLP_DECIMALS);

  const needApproval =
    isBuying && swapTokenAddress !== AddressZero && tokenAllowance && swapAmount && swapAmount.gt(tokenAllowance);

  const swapUsdMin = getUsd(swapAmount, swapTokenAddress, false, infoTokens);
  const clpUsdMax = clpAmount && clpPrice ? clpAmount.mul(clpPrice).div(expandDecimals(1, CLP_DECIMALS)) : undefined;

  let isSwapTokenCapReached;
  if (swapTokenInfo.managedUsd && swapTokenInfo.maxUsdgAmount) {
    isSwapTokenCapReached = swapTokenInfo.managedUsd.gt(
      adjustForDecimals(swapTokenInfo.maxUsdgAmount, USDG_DECIMALS, USD_DECIMALS)
    );
  }

  const onSwapValueChange = (e) => {
    setAnchorOnSwapAmount(true);
    setSwapValue(e.target.value);
  };

  const onClpValueChange = (e) => {
    setAnchorOnSwapAmount(false);
    setClpValue(e.target.value);
  };

  const onSelectSwapToken = (token) => {
    setSwapTokenAddress(token.address);
    setIsWaitingForApproval(false);
  };

  const nativeToken = getTokenInfo(infoTokens, AddressZero);

  let totalApr = bigNumberify(0);

  let feeClpTrackerAnnualRewardsUsd;
  let feeClpTrackerApr;
  if (
    stakingData &&
    stakingData.feeClpTracker &&
    stakingData.feeClpTracker.tokensPerInterval &&
    nativeToken &&
    nativeToken.minPrice &&
    clpSupplyUsd &&
    clpSupplyUsd.gt(0)
  ) {
    feeClpTrackerAnnualRewardsUsd = stakingData.feeClpTracker.tokensPerInterval
      .mul(SECONDS_PER_YEAR)
      .mul(nativeToken.minPrice)
      .div(expandDecimals(1, 18));
    feeClpTrackerApr = feeClpTrackerAnnualRewardsUsd.mul(BASIS_POINTS_DIVISOR).div(clpSupplyUsd);
    totalApr = totalApr.add(feeClpTrackerApr);
  }

  let stakedClpTrackerAnnualRewardsUsd;
  let stakedClpTrackerApr;

  if (
    cadPrice &&
    stakingData &&
    stakingData.stakedClpTracker &&
    stakingData.stakedClpTracker.tokensPerInterval &&
    clpSupplyUsd &&
    clpSupplyUsd.gt(0)
  ) {
    stakedClpTrackerAnnualRewardsUsd = stakingData.stakedClpTracker.tokensPerInterval
      .mul(SECONDS_PER_YEAR)
      .mul(cadPrice)
      .div(expandDecimals(1, 18));
    stakedClpTrackerApr = stakedClpTrackerAnnualRewardsUsd.mul(BASIS_POINTS_DIVISOR).div(clpSupplyUsd);
    totalApr = totalApr.add(stakedClpTrackerApr);
  }

  useEffect(() => {
    const updateSwapAmounts = () => {
      if (anchorOnSwapAmount) {
        if (!swapAmount) {
          setClpValue("");
          setFeeBasisPoints("");
          return;
        }

        if (isBuying) {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getBuyClpToAmount(
            swapAmount,
            swapTokenAddress,
            infoTokens,
            clpPrice,
            usdgSupply,
            totalTokenWeights
          );
          const nextValue = formatAmountFree(nextAmount, CLP_DECIMALS, CLP_DECIMALS);
          setClpValue(nextValue);
          setFeeBasisPoints(feeBps);
        } else {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getSellClpFromAmount(
            swapAmount,
            swapTokenAddress,
            infoTokens,
            clpPrice,
            usdgSupply,
            totalTokenWeights
          );
          const nextValue = formatAmountFree(nextAmount, CLP_DECIMALS, CLP_DECIMALS);
          setClpValue(nextValue);
          setFeeBasisPoints(feeBps);
        }

        return;
      }

      if (!clpAmount) {
        setSwapValue("");
        setFeeBasisPoints("");
        return;
      }

      if (swapToken) {
        if (isBuying) {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getBuyClpFromAmount(
            clpAmount,
            swapTokenAddress,
            infoTokens,
            clpPrice,
            usdgSupply,
            totalTokenWeights
          );
          const nextValue = formatAmountFree(nextAmount, swapToken.decimals, swapToken.decimals);
          setSwapValue(nextValue);
          setFeeBasisPoints(feeBps);
        } else {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getSellClpToAmount(
            clpAmount,
            swapTokenAddress,
            infoTokens,
            clpPrice,
            usdgSupply,
            totalTokenWeights,
            true
          );

          const nextValue = formatAmountFree(nextAmount, swapToken.decimals, swapToken.decimals);
          setSwapValue(nextValue);
          setFeeBasisPoints(feeBps);
        }
      }
    };

    updateSwapAmounts();
  }, [
    isBuying,
    anchorOnSwapAmount,
    swapAmount,
    clpAmount,
    swapToken,
    swapTokenAddress,
    infoTokens,
    clpPrice,
    usdgSupply,
    totalTokenWeights,
  ]);

  const switchSwapOption = (status) => {
    setIsBuying(!!status);
  };

  const fillMaxAmount = () => {
    if (isBuying) {
      setAnchorOnSwapAmount(true);
      setSwapValue(formatAmountFree(swapTokenBalance, swapToken.decimals, swapToken.decimals));
      return;
    }

    setAnchorOnSwapAmount(false);
    setClpValue(formatAmountFree(maxSellAmount, CLP_DECIMALS, CLP_DECIMALS));
  };

  const getError = () => {
    if (IS_NETWORK_DISABLED[chainId]) {
      if (isBuying) return [t`CLP buy disabled, pending ${getChainName(chainId)} upgrade`];
      return [t`CLP sell disabled, pending ${getChainName(chainId)} upgrade`];
    }

    if (!isBuying && inCooldownWindow) {
      return [t`Redemption time not yet reached`];
    }

    if (!swapAmount || swapAmount.eq(0)) {
      return [t`Enter an amount`];
    }
    if (!clpAmount || clpAmount.eq(0)) {
      return [t`Enter an amount`];
    }

    if (isBuying) {
      const swapTokenInfo = getTokenInfo(infoTokens, swapTokenAddress);
      if (swapTokenInfo && swapTokenInfo.balance && swapAmount && swapAmount.gt(swapTokenInfo.balance)) {
        return [t`Insufficient ${swapTokenInfo.symbol} balance`];
      }

      if (swapTokenInfo.maxUsdgAmount && swapTokenInfo.usdgAmount && swapUsdMin) {
        const usdgFromAmount = adjustForDecimals(swapUsdMin, USD_DECIMALS, USDG_DECIMALS);
        const nextUsdgAmount = swapTokenInfo.usdgAmount.add(usdgFromAmount);
        if (swapTokenInfo.maxUsdgAmount.gt(0) && nextUsdgAmount.gt(swapTokenInfo.maxUsdgAmount)) {
          return [t`${swapTokenInfo.symbol} pool exceeded, try different token`, true];
        }
      }
    }

    if (!isBuying) {
      if (maxSellAmount && clpAmount && clpAmount.gt(maxSellAmount)) {
        return [t`Insufficient CLP balance`];
      }

      const swapTokenInfo = getTokenInfo(infoTokens, swapTokenAddress);
      if (
        swapTokenInfo &&
        swapTokenInfo.availableAmount &&
        swapAmount &&
        swapAmount.gt(swapTokenInfo.availableAmount)
      ) {
        return [t`Insufficient liquidity`];
      }
    }

    return [false];
  };

  const isPrimaryEnabled = () => {
    if (IS_NETWORK_DISABLED[chainId]) {
      return false;
    }
    const [error, modal] = getError();
    if (error && !modal) {
      return false;
    }
    if ((needApproval && isWaitingForApproval) || isApproving) {
      return false;
    }
    if (isApproving) {
      return false;
    }
    if (isSubmitting) {
      return false;
    }
    if (isBuying && isSwapTokenCapReached) {
      return false;
    }

    return true;
  };

  const getPrimaryText = () => {
    const [error, modal] = getError();
    if (error && !modal) {
      return error;
    }
    if (isBuying && isSwapTokenCapReached) {
      return t`Max Capacity for ${swapToken.symbol} Reached`;
    }

    if (needApproval && isWaitingForApproval) {
      return t`Waiting for Approval`;
    }
    if (isApproving) {
      return t`Approving ${swapToken.symbol}...`;
    }
    if (needApproval) {
      return t`Approve ${swapToken.symbol}`;
    }

    if (isSubmitting) {
      return isBuying ? t`Buying...` : t`Selling...`;
    }

    return isBuying ? t`Buy CLP` : t`Sell CLP`;
  };

  const approveFromToken = () => {
    approveTokens({
      setIsApproving,
      library,
      tokenAddress: swapToken.address,
      spender: clpManagerAddress,
      chainId: chainId,
      onApproveSubmitted: () => {
        setIsWaitingForApproval(true);
      },
      infoTokens,
      getTokenInfo,
    });
  };

  const buyClp = () => {
    setIsSubmitting(true);

    const minClp = clpAmount.mul(BASIS_POINTS_DIVISOR - savedSlippageAmount).div(BASIS_POINTS_DIVISOR);

    const contract = new ethers.Contract(clpRewardRouterAddress, RewardRouter.abi, library.getSigner());
    const method = swapTokenAddress === AddressZero ? "mintAndStakeClpETH" : "mintAndStakeClp";
    const params = swapTokenAddress === AddressZero ? [0, minClp] : [swapTokenAddress, swapAmount, 0, minClp];
    const value = swapTokenAddress === AddressZero ? swapAmount : 0;

    callContract(chainId, contract, method, params, {
      value,
      sentMsg: t`Buy submitted.`,
      failMsg: t`Buy failed.`,
      successMsg: t`${formatAmount(clpAmount, 18, 4, true)} CLP bought with ${formatAmount(
        swapAmount,
        swapTokenInfo.decimals,
        4,
        true
      )} ${swapTokenInfo.symbol}!`,
      setPendingTxns,
    })
      .then(async () => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const sellClp = () => {
    setIsSubmitting(true);

    const minOut = swapAmount.mul(BASIS_POINTS_DIVISOR - savedSlippageAmount).div(BASIS_POINTS_DIVISOR);

    const contract = new ethers.Contract(clpRewardRouterAddress, RewardRouter.abi, library.getSigner());
    const method = swapTokenAddress === AddressZero ? "unstakeAndRedeemClpETH" : "unstakeAndRedeemClp";
    const params =
      swapTokenAddress === AddressZero ? [clpAmount, minOut, account] : [swapTokenAddress, clpAmount, minOut, account];

    callContract(chainId, contract, method, params, {
      sentMsg: t`Sell submitted!`,
      failMsg: t`Sell failed.`,
      successMsg: t`${formatAmount(clpAmount, 18, 4, true)} CLP sold for ${formatAmount(
        swapAmount,
        swapTokenInfo.decimals,
        4,
        true
      )} ${swapTokenInfo.symbol}!`,
      setPendingTxns,
    })
      .then(async () => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onClickPrimary = () => {
    if (needApproval) {
      approveFromToken();
      return;
    }

    const [, modal] = getError();

    if (modal) {
      setModalError(true);
      return;
    }

    if (isBuying) {
      buyClp();
    } else {
      sellClp();
    }
  };

  let payLabel = t`Pay`;
  let receiveLabel = t`Receive`;
  let payBalance = "$0.00";
  let receiveBalance = "$0.00";
  if (isBuying) {
    if (swapUsdMin) {
      payBalance = `$${formatAmount(swapUsdMin, USD_DECIMALS, 2, true)}`;
    }
    if (clpUsdMax) {
      receiveBalance = `$${formatAmount(clpUsdMax, USD_DECIMALS, 2, true)}`;
    }
  } else {
    if (clpUsdMax) {
      payBalance = `$${formatAmount(clpUsdMax, USD_DECIMALS, 2, true)}`;
    }
    if (swapUsdMin) {
      receiveBalance = `$${formatAmount(swapUsdMin, USD_DECIMALS, 2, true)}`;
    }
  }

  let feePercentageText = formatAmount(feeBasisPoints, 2, 2, true, "-");
  if (feeBasisPoints !== undefined && feeBasisPoints.toString().length > 0) {
    feePercentageText += "%";
  }

  const clpIcon = getIcon("common", "clp");

  return (
    <ModalBase onClose={onClose} className={classes.wrapper}>
      <>
        <div className={classes.toggleWrapper}>
          {/* <span
            onClick={() => switchSwapOption(true)}
            className={classNames(classes.toggleButton, isBuying ? classes.toggleButtonActive : "")}
          >
            Buy CLP
          </span> */}
          <span
            onClick={() => switchSwapOption(false)}
            className={classNames(classes.toggleButton, !isBuying ? classes.toggleButtonActive : "")}
          >
            Sell CLP
          </span>
        </div>

        <div className={classes.inputsWrapper}>
          {isBuying && (
            <BuyInputSection
              topLeftLabel={payLabel}
              topRightLabel={t`Balance:`}
              tokenBalance={`${formatAmount(swapTokenBalance, swapToken.decimals, 4, true)}`}
              inputValue={swapValue}
              onInputValueChange={onSwapValueChange}
              showMaxButton={swapValue !== formatAmountFree(swapTokenBalance, swapToken.decimals, swapToken.decimals)}
              onClickTopRightLabel={fillMaxAmount}
              onClickMax={fillMaxAmount}
              selectedToken={swapToken}
              balance={payBalance}
            >
              <TokenSelector
                label={t`Pay`}
                chainId={chainId}
                tokenAddress={swapTokenAddress}
                onSelectToken={onSelectSwapToken}
                tokens={whitelistedTokens}
                infoTokens={infoTokens}
                className="ClpSwap-from-token"
                showSymbolImage={true}
                showTokenImgInDropdown={true}
              />
            </BuyInputSection>
          )}

          {!isBuying && (
            <BuyInputSection
              topLeftLabel={payLabel}
              topRightLabel={t`Available:`}
              tokenBalance={`${formatAmount(maxSellAmount, CLP_DECIMALS, 4, true)}`}
              inputValue={clpValue}
              onInputValueChange={onClpValueChange}
              showMaxButton={clpValue !== formatAmountFree(maxSellAmount, CLP_DECIMALS, CLP_DECIMALS)}
              onClickTopRightLabel={fillMaxAmount}
              onClickMax={fillMaxAmount}
              balance={payBalance}
              defaultTokenName={"CLP"}
            >
              <div className={classes.tokenButtonWrapper}>
                <img width={24} height={24} src={clpIcon} alt="" /> CLP
              </div>
            </BuyInputSection>
          )}

          <div
            onClick={() => {
              setIsBuying((prevState) => !prevState);
            }}
            className={classes.arrowContainer}
          >
            <img src={ArrowIcon} />
          </div>

          {isBuying ? (
            <BuyInputSection
              topLeftLabel={receiveLabel}
              topRightLabel={t`Balance:`}
              tokenBalance={`${formatAmount(clpBalance, CLP_DECIMALS, 4, true)}`}
              inputValue={clpValue}
              onInputValueChange={onClpValueChange}
              balance={receiveBalance}
              defaultTokenName={"CLP"}
            >
              <div className={classes.tokenButtonWrapper}>
                <img width={24} height={24} src={clpIcon} alt="" /> CLP
              </div>
            </BuyInputSection>
          ) : (
            <BuyInputSection
              topLeftLabel={receiveLabel}
              topRightLabel={t`Balance:`}
              tokenBalance={`${formatAmount(swapTokenBalance, swapToken.decimals, 4, true)}`}
              inputValue={swapValue}
              onInputValueChange={onSwapValueChange}
              balance={receiveBalance}
              selectedToken={swapToken}
            >
              <TokenSelector
                label={t`Receive`}
                chainId={chainId}
                tokenAddress={swapTokenAddress}
                onSelectToken={onSelectSwapToken}
                tokens={whitelistedTokens}
                infoTokens={infoTokens}
                className="ClpSwap-from-token"
                showSymbolImage={true}
                showTokenImgInDropdown={true}
              />
            </BuyInputSection>
          )}
        </div>

        <div>
          <div className={classes.feesWrapper}>
            <div className={classes.feesLabel}>{feeBasisPoints > 50 ? t`WARNING: High Fees` : t`Fees`}</div>
            <div className={classes.feesContent}>
              {isBuying && (
                <Tooltip
                  handle={isBuying && isSwapTokenCapReached ? "NA" : feePercentageText}
                  position="right-bottom"
                  renderContent={() => {
                    if (!feeBasisPoints) {
                      return (
                        <div className="text-white">
                          <Trans>Fees will be shown once you have entered an amount in the order form.</Trans>
                        </div>
                      );
                    }
                    return (
                      <div className="text-white">
                        {feeBasisPoints > 50 && <Trans>To reduce fees, select a different asset to pay with.</Trans>}
                        <Trans>Check the "Save on Fees" section below to get the lowest fee percentages.</Trans>
                      </div>
                    );
                  }}
                />
              )}
              {!isBuying && (
                <Tooltip
                  handle={feePercentageText}
                  position="right-bottom"
                  renderContent={() => {
                    if (!feeBasisPoints) {
                      return (
                        <div className="text-white">
                          <Trans>Fees will be shown once you have entered an amount in the order form.</Trans>
                        </div>
                      );
                    }
                    return (
                      <div className="text-white">
                        {feeBasisPoints > 50 && <Trans>To reduce fees, select a different asset to receive.</Trans>}
                        <Trans>Check the "Save on Fees" section below to get the lowest fee percentages.</Trans>
                      </div>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <ActionButton
          onClick={onClickPrimary}
          type={"outlined"}
          buttonText={getPrimaryText()}
          disabled={!isPrimaryEnabled()}
          className={classes.button}
        />
      </>
      <SwapErrorModal
        isVisible={Boolean(modalError)}
        setIsVisible={setModalError}
        swapToken={swapToken}
        chainId={chainId}
        clpAmount={clpAmount}
        usdgSupply={usdgSupply}
        totalTokenWeights={totalTokenWeights}
        clpPrice={clpPrice}
        infoTokens={infoTokens}
        swapUsdMin={swapUsdMin}
      />
    </ModalBase>
  );
}
